import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Image, Modal, Row } from 'react-bootstrap';
import i18next from 'i18next';
import AvatarCropper from './AvatarCropper';
import AppModal from './app-modal/AppModal';

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch: RegExpMatchArray | null = header.match(/data:(.*?);base64/);
  const mime: string = mimeMatch ? mimeMatch[1] : 'image/png';

  const binaryString: string = atob(base64);
  const lenght: number = binaryString.length;
  const bufferArray = new Uint8Array(lenght);

  for (let i: number = 0; i < lenght; i++) {
    bufferArray[i] = binaryString.charCodeAt(i);
  }

  return new File([bufferArray], filename, { type: mime });
}

type AvatarModalProps = {
  isShow: boolean;
  src: string | null;
  onPositive: (file: File, dataUrl: string) => void;
  onNegative: () => void;
  maxFileSize?: number;
};

const AvatarModal: FC<AvatarModalProps> = ({
  isShow,
  src,
  onPositive,
  onNegative,
  maxFileSize = 1_000_000
}: AvatarModalProps): ReactElement => {
  const [sourceImageUrl, setSourceImageUrl] = useState<string | null>(src);

  const [livePreviewDataUrl, setLivePreviewDataUrl] = useState<string | null>(null);

  const [isValid, setIsValid] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastObjectUrlRef = useRef<string |null>(null);

  useEffect(() => {
    if (src) {
      setSourceImageUrl(src);
      setLivePreviewDataUrl(null);
    }
  }, [src]);

  useEffect(() => {
    return (): void => {
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
      }
    };
  }, []);

  const handleClose = (): void => {
    onNegative();
  };

  const handleUpload = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file: File | undefined = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > maxFileSize) {
      setIsValid(false);
      setErrorMessage(i18next.t('avatarModal.fileTooBig'));
      return;
    }

    setIsValid(true);
    setErrorMessage(null);

    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
    }

    const blobUrl: string = URL.createObjectURL(file);
    lastObjectUrlRef.current = blobUrl;
    setSourceImageUrl(blobUrl);
    setLivePreviewDataUrl(null);
  };

  const handlePreviewChange = useCallback((dataUrl: string) => {
    setLivePreviewDataUrl(dataUrl);
  }, []);

  const handleAccept = (): void => {
    if (!livePreviewDataUrl) {
      return;
    }

    const file: File = dataUrlToFile(livePreviewDataUrl, 'avatar.png');
    onPositive(file, livePreviewDataUrl);
  };

  return (
    <AppModal
      size="lg"
      show={isShow}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('avatarModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <input
          ref={fileInputRef}
          className="d-none"
          type="file"
          accept="image/*"
          onChange={handleFileSelected}
        />

        {errorMessage && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger">{errorMessage}</Alert>
            </Col>
          </Row>
        )}

        <Row className="g-4 flex-column flex-md-row">
          <Col md={8}>
            <AvatarCropper
              srcImage={sourceImageUrl ?? null}
              overlayOpacity={0.35}
              ringWidth={2}
              onPreviewChange={handlePreviewChange}
            />
          </Col>

          <Col md={4} className="text-center d-flex flex-column align-items-center justify-content-start">
            <div className="mb-3 fw-semibold">
              {i18next.t('avatarModal.preview')}
            </div>

            <div
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                backgroundColor: '#F8F9FA',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Image
                src={livePreviewDataUrl ? livePreviewDataUrl : (src ?? '/default_profile.png')}
                alt=""
                roundedCircle
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>

            <Button
              className="mt-4 px-3 py-2"
              style={{
                whiteSpace: 'nowrap',
                backgroundColor: '#FF695F',
                borderColor: '#FF695F',
              }}
              onClick={handleUpload}
            >
              <i className="fas fa-upload" />{' '}
              {i18next.t('avatarModal.chooseFile')}
            </Button>

            <div className="text-muted mt-2" style={{ fontSize: '1rem' }}>
              {i18next.t('avatarModal.info')}
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {i18next.t('generic.cancel')}
        </Button>

        <Button variant="primary" onClick={handleAccept} disabled={!isValid || !livePreviewDataUrl}>
          {i18next.t('generic.save')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};

export default AvatarModal;
